import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './AmazonAdsUploadModal.module.css';
import '../../../styles/buttons.css';
import { useToast } from '../../../hooks/useToast';
import { apiClient } from '../../../services/apiClient';
import { generateDownloadFilename, getFileExtension } from './downloadUtils';
import { brandConfig } from '../../../config/brands';
import type { MaterialData } from './types';

interface AmazonAdsAccount {
  id: string;
  profile_id: string;
  profile_name: string;
  country_code: string;
  account_type: string;
}

interface AmazonAdsBrand {
  brand_entity_id: string;
  brand_name: string;
}

interface AsinItem {
  asin: string;
  main_image_url: string | null;
  title: string | null;
}

interface AmazonAdsCreativeRecord {
  id: string;
  cal_asset_id: string | null;
  asset_url: string | null;
  asset_type: 'IMAGE' | 'VIDEO' | null;
  asset_subtype: string | null;
  brand_name_snapshot: string | null;
  upload_status: string | null;
  moderation_status: string | null;
  moderation_status_map: Record<string, string> | null;
  last_moderation_check_at: string | null;
  created_at: string;
}

const ASSET_SUBTYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'LOGO', label: 'ロゴ' },
  { value: 'PRODUCT_IMAGE', label: '商品画像' },
  { value: 'AUTHOR_IMAGE', label: '著者画像' },
  { value: 'LIFESTYLE_IMAGE', label: 'ライフスタイル画像' },
  { value: 'OTHER_IMAGE', label: 'その他の画像' },
];

interface AmazonAdsUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  materialId: string;
  materialUrl: string;
  material?: MaterialData;
  onUploadSuccess?: () => void;
}

export function AmazonAdsUploadModal({
  isOpen,
  onClose,
  materialId,
  material,
  onUploadSuccess,
}: AmazonAdsUploadModalProps) {
  const toast = useToast();
  const [accounts, setAccounts] = useState<AmazonAdsAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [creativeName, setCreativeName] = useState('');
  const [brands, setBrands] = useState<AmazonAdsBrand[]>([]);
  const [selectedBrandEntityId, setSelectedBrandEntityId] = useState<string>('');
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);
  const [assetSubtype, setAssetSubtype] = useState<string>('OTHER_IMAGE');
  const [asinItems, setAsinItems] = useState<AsinItem[]>([]);
  const [asinAddInput, setAsinAddInput] = useState('');
  const [isAddingAsin, setIsAddingAsin] = useState(false);
  const [productBrandName, setProductBrandName] = useState<string>(''); // material から推定したブランド名
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [requiredPoints, setRequiredPoints] = useState<number>(0);
  const [isUnlimitedPlan, setIsUnlimitedPlan] = useState<boolean>(false);

  // モーダル open 時に消費レートを取得 (credit_rate_config 動的反映)
  useEffect(() => {
    if (!isOpen) return;
    const fetchRate = async () => {
      try {
        const { data } = await apiClient.get<{
          is_unlimited: boolean;
          credits_per_call: number;
        }>('/amazon-ads/credit-rate');
        setIsUnlimitedPlan(!!data.is_unlimited);
        setRequiredPoints(Number(data.credits_per_call) || 0);
      } catch (error) {
        console.error('Failed to fetch credit rate:', error);
      }
    };
    fetchRate();
  }, [isOpen]);

  // モーダルが開いたタイミングで、ダウンロード時と同じファイル名をデフォルト値にする
  useEffect(() => {
    if (!isOpen || !material) return;
    setCreativeName((current) => {
      if (current.trim()) return current;
      const contentType = material.content_type || 'image/png';
      const extension = getFileExtension(contentType);
      return generateDownloadFilename(material, contentType, extension);
    });
  }, [isOpen, material]);

  // アカウント一覧を取得
  useEffect(() => {
    if (!isOpen) return;

    const fetchAccounts = async () => {
      try {
        setIsLoadingAccounts(true);
        const { data } = await apiClient.get<{ accounts?: AmazonAdsAccount[] }>(
          '/amazon-ads/accounts'
        );
        setAccounts(data.accounts || []);

        if (data.accounts && data.accounts.length > 0) {
          setSelectedAccountId(data.accounts[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch Amazon Ads accounts:', error);
        toast.show('広告アカウントの取得に失敗しました');
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    fetchAccounts();
  }, [isOpen]);

  // マテリアルに紐づく商品情報を取得
  useEffect(() => {
    if (!isOpen) return;

    const fetchProductInfo = async () => {
      try {
        const { data } = await apiClient.get<{
          brand_name?: string;
          asins?: string[];
          products?: AsinItem[];
        }>(`/amazon-ads/materials/${materialId}/product-info`);

        if (data.brand_name) {
          setProductBrandName(data.brand_name);
        }
        if (data.products && data.products.length > 0) {
          setAsinItems(data.products);
        } else if (data.asins && data.asins.length > 0) {
          setAsinItems(data.asins.map((a) => ({ asin: a, main_image_url: null, title: null })));
        }
      } catch (error) {
        console.error('Failed to fetch product info:', error);
      }
    };

    fetchProductInfo();
  }, [isOpen, materialId]);

  // 選択中アカウントのブランド一覧を取得
  useEffect(() => {
    if (!isOpen || !selectedAccountId) {
      setBrands([]);
      setSelectedBrandEntityId('');
      return;
    }

    const fetchBrands = async () => {
      try {
        setIsLoadingBrands(true);
        const { data } = await apiClient.get<{ brands?: AmazonAdsBrand[] }>(
          `/amazon-ads/accounts/${selectedAccountId}/brands`
        );
        const list = data.brands || [];
        setBrands(list);

        // material 由来のブランド名と完全一致したら自動選択
        if (productBrandName) {
          const match = list.find(
            (b) => b.brand_name.toLowerCase() === productBrandName.toLowerCase()
          );
          setSelectedBrandEntityId(match ? match.brand_entity_id : '');
        }
      } catch (error) {
        console.error('Failed to fetch brands:', error);
        // ブランドが取れなくてもアップロード自体は可能なのでトーストは出さない
        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    };

    fetchBrands();
  }, [isOpen, selectedAccountId, productBrandName]);

  const handleRemoveAsin = (asin: string) => {
    setAsinItems((prev) => prev.filter((item) => item.asin !== asin));
  };

  const handleAddAsin = async () => {
    const raw = asinAddInput.trim().toUpperCase();
    if (!raw) return;
    // カンマ区切り or スペース区切りで複数受け付ける
    const candidates = raw
      .split(/[\s,]+/)
      .map((a) => a.trim())
      .filter((a) => a.length > 0);
    const existing = new Set(asinItems.map((item) => item.asin));
    const newAsins = candidates.filter((a) => !existing.has(a));
    if (newAsins.length === 0) {
      setAsinAddInput('');
      return;
    }

    // とりあえずサムネイルなしで先に追加 (UI 即時反映)
    setAsinItems((prev) => [
      ...prev,
      ...newAsins.map((a) => ({ asin: a, main_image_url: null, title: null })),
    ]);
    setAsinAddInput('');

    // バックエンドからサムネイルを引いて埋める
    try {
      setIsAddingAsin(true);
      const { data } = await apiClient.get<{ products?: AsinItem[] }>(
        `/amazon-ads/products/lookup?asins=${encodeURIComponent(newAsins.join(','))}`
      );
      const lookupMap = new Map(
        (data.products || []).map((p) => [p.asin, p])
      );
      setAsinItems((prev) =>
        prev.map((item) => {
          const found = lookupMap.get(item.asin);
          if (found && (found.main_image_url || found.title)) {
            return {
              asin: item.asin,
              main_image_url: found.main_image_url ?? item.main_image_url,
              title: found.title ?? item.title,
            };
          }
          return item;
        })
      );
    } catch (error) {
      console.error('Failed to lookup ASIN thumbnails:', error);
    } finally {
      setIsAddingAsin(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedAccountId) {
      toast.show('広告アカウントを選択してください');
      return;
    }

    if (!creativeName.trim()) {
      toast.show('クリエイティブ名を入力してください');
      return;
    }

    try {
      setIsUploading(true);

      const asinList = asinItems.map((item) => item.asin).filter((a) => a.length > 0);

      const selectedBrand = brands.find((b) => b.brand_entity_id === selectedBrandEntityId);

      const { data } = await apiClient.post<{
        points_consumed?: number;
        moderation_status?: string;
      }>('/amazon-ads/creatives/upload', {
        account_id: selectedAccountId,
        material_id: materialId,
        creative_name: creativeName,
        brand_entity_id: selectedBrandEntityId || undefined,
        brand_name: selectedBrand?.brand_name || undefined,
        asset_subtype: assetSubtype || undefined,
        asin_list: asinList.length > 0 ? asinList : undefined,
      });

      toast.show(
        `アップロードしました (Amazon コンソールで「審査をリクエストする」を押してください / ${data.points_consumed || 0}pt消費)`
      );

      if (onUploadSuccess) {
        onUploadSuccess();
      }

      onClose();
    } catch (error: any) {
      console.error('Failed to upload to Amazon Ads:', error);
      const message =
        error?.response?.data?.error || error?.message || 'アップロードに失敗しました';
      toast.show(message);
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>Amazon Ads クリエイティブアセットにアップロード</h2>
          <p className={styles.lead}>
            スポンサーディスプレイ広告やスポンサーブランド広告のクリエイティブアセットとしてアップロードします。
          </p>
          <p className={styles.description}>
            ※この画面ではスポンサープロダクト広告クリエイティブはアップロードできません。{brandConfig.labels.products}から商品画像の差し替えを行なってください。
          </p>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          {isLoadingAccounts ? (
            <p>読み込み中...</p>
          ) : accounts.length === 0 ? (
            <div>
              <p>広告アカウントが接続されていません。</p>
              <a href="/connections/amazon-ads" style={{ color: '#0066cc' }}>
                広告アカウントを接続
              </a>
            </div>
          ) : (
            <div className={styles.bodyGrid}>
              <div className={styles.bodyColumn}>
                <p className={styles.columnLabel}>クリエイティブ情報</p>

                <div className="form-group">
                  <label className="form-label">広告アカウント</label>
                  <select
                    className="form-select"
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                  >
                    {accounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.profile_name || account.profile_id} ({account.country_code})
                      </option>
                    ))}
                  </select>
                </div>

                <div className={`form-group ${styles.fieldWide}`}>
                  <label className="form-label">クリエイティブ名</label>
                  <input
                    type="text"
                    className="form-input"
                    value={creativeName}
                    onChange={(e) => setCreativeName(e.target.value)}
                    placeholder="例: 商品名 - 夏季キャンペーン"
                  />
                </div>

                <div className={`form-group ${styles.fieldShort}`}>
                  <label className="form-label">ブランド (任意)</label>
                  <select
                    className="form-select"
                    value={selectedBrandEntityId}
                    onChange={(e) => setSelectedBrandEntityId(e.target.value)}
                    disabled={isLoadingBrands}
                  >
                    <option value="">
                      {isLoadingBrands ? 'ブランド一覧を取得中...' : '選択しない (SB / Stores では利用不可になります)'}
                    </option>
                    {brands.map((brand) => (
                      <option key={brand.brand_entity_id} value={brand.brand_entity_id}>
                        {brand.brand_name}
                      </option>
                    ))}
                  </select>
                  <p className={styles.fieldHelp}>
                    Amazon Brand Registry に登録されているブランドのみ選択可能です。
                  </p>
                </div>

                <div className={`form-group ${styles.fieldShort}`}>
                  <label className="form-label">アセットタイプ</label>
                  <select
                    className="form-select"
                    value={assetSubtype}
                    onChange={(e) => setAssetSubtype(e.target.value)}
                  >
                    {ASSET_SUBTYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.bodyColumn}>
                <p className={styles.columnLabel}>関連商品 (ASIN)</p>

                <div className="form-group">
                  {asinItems.length === 0 ? (
                    <div className={styles.asinEmpty}>関連 ASIN は登録されていません</div>
                  ) : (
                    <div className={styles.asinList}>
                      {asinItems.map((item) => (
                        <div key={item.asin} className={styles.asinItem}>
                          <div className={styles.asinThumbnail}>
                            {item.main_image_url ? (
                              <img src={item.main_image_url} alt={item.asin} />
                            ) : (
                              <span>—</span>
                            )}
                          </div>
                          <div className={styles.asinInfo}>
                            <div className={styles.asinCode}>{item.asin}</div>
                            {item.title && <div className={styles.asinTitle}>{item.title}</div>}
                          </div>
                          <button
                            type="button"
                            className={styles.asinRemove}
                            onClick={() => handleRemoveAsin(item.asin)}
                            aria-label={`${item.asin} を削除`}
                            title="削除"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={styles.asinAddRow}>
                    <input
                      type="text"
                      className="form-input"
                      value={asinAddInput}
                      onChange={(e) => setAsinAddInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          void handleAddAsin();
                        }
                      }}
                      placeholder="ASIN を追加 (例: B0CK5JP6D7)"
                      disabled={isAddingAsin}
                    />
                    <button
                      type="button"
                      className="btn-modal-secondary"
                      onClick={() => void handleAddAsin()}
                      disabled={isAddingAsin || !asinAddInput.trim()}
                    >
                      追加
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            type="button"
            className="btn-cancel"
            onClick={onClose}
            disabled={isUploading}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="btn-modal-secondary"
            onClick={() => setIsHistoryOpen(true)}
            disabled={isUploading}
          >
            履歴
          </button>
          <button
            type="button"
            className="btn-modal-success"
            onClick={handleUpload}
            disabled={isUploading || accounts.length === 0 || !creativeName.trim()}
          >
            {isUploading ? 'アップロード中...' : 'アップロード'}
            {isUnlimitedPlan ? (
              <span className="credits-badge">無料</span>
            ) : requiredPoints > 0 ? (
              <span className="credits-badge">{requiredPoints}</span>
            ) : null}
          </button>
        </div>
      </div>

      <AmazonAdsCreativesHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
      />
    </div>,
    document.body
  );
}

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function AmazonAdsCreativesHistoryModal({ isOpen, onClose }: HistoryModalProps) {
  const toast = useToast();
  const [creatives, setCreatives] = useState<AmazonAdsCreativeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const fetchCreatives = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data } = await apiClient.get<{ creatives?: AmazonAdsCreativeRecord[] }>(
        '/amazon-ads/creatives'
      );
      setCreatives(data.creatives || []);
    } catch (error) {
      console.error('Failed to fetch creatives:', error);
      toast.show('履歴の取得に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (isOpen) fetchCreatives();
  }, [isOpen, fetchCreatives]);

  const handleRefresh = async (id: string) => {
    try {
      setRefreshingId(id);
      const { data } = await apiClient.post<{
        moderation_status?: string;
        moderation_status_map?: Record<string, string>;
      }>(`/amazon-ads/creatives/${id}/refresh-moderation`);
      setCreatives((prev) =>
        prev.map((c) =>
          c.id === id
            ? {
                ...c,
                moderation_status: data.moderation_status || c.moderation_status,
                moderation_status_map: data.moderation_status_map || c.moderation_status_map,
                last_moderation_check_at: new Date().toISOString(),
              }
            : c
        )
      );
    } catch (error: any) {
      const message = error?.response?.data?.error || 'ステータス取得に失敗しました';
      toast.show(message);
    } finally {
      setRefreshingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={styles.backdrop}
      onClick={onClose}
      style={{ zIndex: 100 }}
    >
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '900px' }}
      >
        <div className={styles.modalHeader}>
          <h2>アップロード履歴と審査ステータス</h2>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.modalBody}>
          {isLoading ? (
            <p>読み込み中...</p>
          ) : creatives.length === 0 ? (
            <p>まだアップロード履歴がありません。</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                  <th style={{ padding: '8px' }}>アップロード日時</th>
                  <th style={{ padding: '8px' }}>ブランド</th>
                  <th style={{ padding: '8px' }}>タイプ</th>
                  <th style={{ padding: '8px' }}>審査</th>
                  <th style={{ padding: '8px' }}>プログラム別</th>
                  <th style={{ padding: '8px' }}></th>
                </tr>
              </thead>
              <tbody>
                {creatives.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>
                      {new Date(c.created_at).toLocaleString('ja-JP')}
                    </td>
                    <td style={{ padding: '8px' }}>{c.brand_name_snapshot || '—'}</td>
                    <td style={{ padding: '8px' }}>
                      {c.asset_subtype || '—'}
                    </td>
                    <td style={{ padding: '8px' }}>
                      <ModerationBadge status={c.moderation_status} />
                    </td>
                    <td style={{ padding: '8px', fontSize: '12px', color: '#555' }}>
                      {c.moderation_status_map &&
                      Object.keys(c.moderation_status_map).length > 0
                        ? Object.entries(c.moderation_status_map)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(', ')
                        : '—'}
                    </td>
                    <td style={{ padding: '8px' }}>
                      <button
                        type="button"
                        onClick={() => handleRefresh(c.id)}
                        disabled={refreshingId === c.id}
                        style={{
                          background: 'none',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          padding: '4px 8px',
                          fontSize: '12px',
                          cursor: 'pointer',
                        }}
                      >
                        {refreshingId === c.id ? '更新中...' : '最新を取得'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button type="button" className="btn-modal" onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
}

function ModerationBadge({ status }: { status: string | null }) {
  if (!status) return <span style={{ color: '#999' }}>—</span>;
  const colorMap: Record<string, { bg: string; fg: string }> = {
    APPROVED: { bg: '#d4edda', fg: '#155724' },
    REJECTED: { bg: '#f8d7da', fg: '#721c24' },
    PENDING: { bg: '#fff3cd', fg: '#856404' },
    MIXED: { bg: '#e2e3e5', fg: '#383d41' },
    UNKNOWN: { bg: '#f1f1f1', fg: '#666' },
  };
  const colors = colorMap[status] || colorMap.UNKNOWN;
  return (
    <span
      style={{
        backgroundColor: colors.bg,
        color: colors.fg,
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
}
